import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Observable, Subject, Subscription, timer} from 'rxjs';
import {filter, map, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {Response} from '../../../models';
import {DialogService, InvoicesService, XeroConnectionService, SnackbarService} from '../../../services';
import {ConfirmDisconnectXeroComponent} from './confirm-disconnect-xero/confirm-disconnect-xero.component';

@Component({
    selector: 'prism-xero-display',
    templateUrl: './xero-display.component.html',
    styleUrls: ['./xero-display.component.scss']
})
export class XeroDisplayComponent implements OnInit, OnDestroy {
    subDestroyer$: Subject<void>;
    loadingQueueCount;
    reconBusySingle$: Observable<boolean>;
    reconBusyQueue$: Observable<boolean>;
    displayXeroControl;
    xeroExpiryTimerSubject$;
    xeroExpiryTimer$: Observable<any>;
    organisation$: Observable<any>;
    invoiceQueueCount$: Observable<number>;
    queueHasError$: Observable<boolean>;
    xeroLoading$: Observable<boolean>;
    loadingQueue$;
    getXeroConnectionDataSub: Subscription;

    constructor(private xeroConnectService: XeroConnectionService,
                private snackbarService: SnackbarService,
                private dialogService: DialogService,
                private router: Router,
                private invoicesService: InvoicesService) {

        this.subDestroyer$ = new Subject<void>();
        this.loadingQueueCount = true;
        this.reconBusySingle$ = this.invoicesService.reconBusySingle$;
        this.reconBusyQueue$ = this.invoicesService.reconBusyQueue$;
        this.displayXeroControl = false;
        this.xeroExpiryTimerSubject$ = this.xeroConnectService.getXeroExpiryTimer();
        this.organisation$ = this.xeroConnectService.organisation$;
        this.invoiceQueueCount$ = this.invoicesService.invoicesInQueueCount
            .pipe(
                startWith(-1)
            );
        this.queueHasError$ = this.invoicesService.queueHasErrors$;
        this.xeroLoading$ = this.xeroConnectService.xeroLoading$;
        this.loadingQueue$ = this.invoicesService.loadingQueue$;
    }

    ngOnInit() {
        this.xeroExpiryTimerSubject$
            .subscribe(timer => {
                this.xeroExpiryTimer$ = timer;
            });
        this.getXeroConnectionDataSub = this.xeroConnectService.getXeroConnection()
            .pipe(
                takeUntil(this.subDestroyer$)
            )
            .subscribe(
                () => {
                },
                () => {
                    this.xeroExpiryTimer$ = timer(0);
                    console.error(`Error getting Xero connection data`);
                }
            );
    }

    connectToXero() {
        this.snackbarService.showSnackbar('Establishing connection with Xero...');
        this.xeroConnectService.getToken()
            .pipe(
                takeUntil(this.subDestroyer$)
            )
            .subscribe(
                () => {
                },
                err => {
                    this.snackbarService.showSnackbar('Failed to connect to Xero.', 2000);
                    console.error('Error getting Xero token:', err);
                }
            );
    }

    disconnectXero() {
        this.dialogService.openDialog(ConfirmDisconnectXeroComponent)
            .afterClosed()
            .pipe(
                filter(confirmed => !!confirmed),
                switchMap(() => this.xeroConnectService.disconnectXero()),
                takeUntil(this.subDestroyer$)
            )
            .subscribe(
                () => {
                    this.snackbarService.showSnackbar('Disconnected from Xero');
                    this.displayXeroControl = false;
                },
                err => {
                    this.snackbarService.showSnackbar('Failed to disconnect from Xero.', 2000);
                    console.error('Error disconnecting from Xero:', err);
                }
            );
    }

    ngOnDestroy() {
        this.subDestroyer$.next();
    }

    toggleXeroInfoDisplay() {
        this.displayXeroControl = !this.displayXeroControl;

        if (this.displayXeroControl) {
            this.loadingQueueCount = true;
            this.invoicesService.getInvoicesQueueCount()
                .subscribe(
                    () => {
                        this.loadingQueueCount = false;
                    },
                    err => {
                        console.error('Error getting queued invoicecount:', err);
                        this.snackbarService.handleError('Could not get the queued invoices count');
                    }
                );

        }
    }

    navToUnsyncedQueue() {
        this.invoicesService.setSelectedInvoiceTab('unsynced');
        this.router.navigate(['/home/invoices'])
            .then(() => this.displayXeroControl = false)
            .catch(err => console.error('Error navigating to invoices page:', err));
    }

    reconInvoices() {
        const sessionHasExpired = this.xeroConnectService.sessionExpired;

        if (!sessionHasExpired) {
            this.invoicesService.updateSyncedInvoices()
                .pipe(
                    map((updateRes: Response) => updateRes.data)
                )
                .toPromise()
                .then(() => {

                    this.invoicesService.getInvoicesQueueCount()
                        .toPromise()
                        .catch(err => {
                            console.error('Error with invoice recon:', err);
                        });

                    this.snackbarService.showSnackbar('Invoice Sync Complete');
                })
                .catch(err => {
                    console.error('Error updating synced invoices:', err);
                });
        }
    }
}
